import { useEffect, useState } from "react";
import { trackEvent } from "../../../helpers/analytics";
import { useLoadLoggedInStatus, useGetLoggedInStatus, ILoggedInStatus } from "../../LoggedInStatus";
import { HUBSPOT_EMAIL_FORM_SUBMITTED_KEY } from "../constants";
import { isClientSideRendering } from "../../../helpers/rendererType";

interface IuseHubspotFormBlocked {
  shouldShowHubspotModal: boolean;
  shouldShowNotUpdatedWarning: boolean;
  tryToIncreaseNumberOfUsage: () => Promise<boolean>;
  onFormSubmitted: (callback?: () => void) => void;
  closeForm: () => void;
}

const shouldLoadLoginStatus = (status: ILoggedInStatus) => status === ILoggedInStatus.UNKNOWN || status === ILoggedInStatus.LOADING;

export const useHubspotFormBlocked = (key: string, numberOfUsagesAllowed: number, initialVisibility = false): IuseHubspotFormBlocked => {
  const hasSubmittedTheForm = isClientSideRendering() && JSON.parse(window.localStorage.getItem(HUBSPOT_EMAIL_FORM_SUBMITTED_KEY) || "false");
  const [shouldShowHubspotModal, setShouldShowHubspotModal] = useState(initialVisibility ? !hasSubmittedTheForm : false);
  const [shouldShowNotUpdatedWarning, setShouldShowNotUpdatedWarning] = useState(false);

  useEffect(() => {
    if (shouldShowHubspotModal) {
      trackEvent("HubspotModal Lead form shown", {
        module: key,
      });
    }
  }, [shouldShowHubspotModal]);

  const loadLoginStatus = useLoadLoggedInStatus();
  const loginStatus = useGetLoggedInStatus();
  const tryToIncreaseNumberOfUsage = async () => {
    const loginStatusFullyLoaded = shouldLoadLoginStatus(loginStatus) ? await loadLoginStatus() : loginStatus;
    const isUserLoggedIn = loginStatusFullyLoaded === ILoggedInStatus.LOGGED_IN;
    const numberOfUsages = Number(localStorage.getItem(key)) || 0;
    if (!isUserLoggedIn && !hasSubmittedTheForm) {
      const newNumberOfUsages = numberOfUsages + 1;
      const shouldShowForm = newNumberOfUsages > numberOfUsagesAllowed;
      if (!shouldShowForm) {
        localStorage.setItem(key, String(newNumberOfUsages));
      }

      setShouldShowHubspotModal(shouldShowForm);
      setShouldShowNotUpdatedWarning(shouldShowForm);
      return shouldShowForm;
    }
    setShouldShowHubspotModal(false);
    setShouldShowNotUpdatedWarning(false);
    return false;
  };
  const onFormSubmitted = (callBack?: () => void) => {
    localStorage.setItem(HUBSPOT_EMAIL_FORM_SUBMITTED_KEY, String(true));
    setShouldShowHubspotModal(false);
    setShouldShowNotUpdatedWarning(false);
    if (callBack && typeof callBack === "function") {
      callBack();
    }
    trackEvent("HubspotModal Lead form submitted", {
      module: key,
    });
  };
  const closeForm = () => {
    setShouldShowHubspotModal(false);
    setShouldShowNotUpdatedWarning(true);
  };

  return {
    shouldShowHubspotModal, tryToIncreaseNumberOfUsage, onFormSubmitted, closeForm, shouldShowNotUpdatedWarning,
  };
};
